/**
 * VaxDocumentViewer (#752) — open the ORIGINAL vaccination record, full
 * screen, readable.
 *
 * Ted (2026-09-25), on the Gingr version of this: "the image is so small
 * that I can't read it, and enlarging it doesn't keep it on screen. I have
 * to text it to myself to view it." So the record opens at the full size of
 * the screen, fitted, and zooming is an explicit control that scrolls
 * inside the viewer — it never pushes the document off the page.
 *
 * The document is fetched with the staff Bearer token (a plain `<img src>`
 * to the API could not authenticate). Two answers come back (#1131):
 *
 *   - a presigned LINK to private object storage → used directly as the
 *     img/iframe src and the "Open in new tab" href. Nothing to revoke; the
 *     link expires on its own in five minutes.
 *   - the BYTES (a Postgres-resident record, or a pre-#1131 server) → an
 *     object URL, exactly as #752 did, revoked on close. Only a URL this
 *     component CREATED is ever revoked.
 *
 * "Open in new tab" hands the same URL to the browser's own viewer — the
 * dependable path for a multi-page PDF on an iPhone, where an embedded PDF
 * shows only its first page.
 */
import { useEffect, useRef, useState } from 'react';
import {
  fetchVaxDocument,
  readSession,
  type VaxDocumentMeta,
  type VaxDocumentSource,
} from '../../lib/api.js';

export interface VaxDocumentViewerProps {
  apiUrl: string;
  /** #752 — an intake original is fetched by its submission + document id. */
  submissionId?: string;
  document: Pick<VaxDocumentMeta, 'id' | 'filename' | 'contentType'>;
  /**
   * Ted #32a (E1.6c) — any other home for the bytes, e.g. a certificate
   * staff attached to a vaccination row. Used instead of the submission
   * fetch when present. Read once per document id (held in a ref), so an
   * inline arrow does not refetch on every render.
   */
  load?: (token: string) => Promise<VaxDocumentSource>;
  /** Heading, e.g. "Diesel — vaccination record". */
  title: string;
  onClose: () => void;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; url: string }
  | { kind: 'error'; message: string };

const ZOOM_STEPS = [1, 1.5, 2, 3, 4] as const;

export function VaxDocumentViewer({
  apiUrl,
  submissionId,
  document: doc,
  load,
  title,
  onClose,
}: VaxDocumentViewerProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [zoomIndex, setZoomIndex] = useState(0);
  const closeRef = useRef<HTMLButtonElement>(null);
  const loadRef = useRef(load);
  loadRef.current = load;
  const isPdf = doc.contentType === 'application/pdf';

  useEffect(() => {
    let cancelled = false;
    // Set ONLY when this effect minted an object URL — a presigned link is
    // not ours to revoke.
    let objectUrl: string | null = null;
    const { token } = readSession();
    if (!token) {
      setState({ kind: 'error', message: 'Your session has ended. Sign in again to open this record.' });
      return;
    }
    const fetchSource = loadRef.current
      ? loadRef.current(token)
      : submissionId
        ? fetchVaxDocument(apiUrl, token, submissionId, doc.id)
        : Promise.reject(new Error('Could not open the record.'));
    fetchSource
      .then((source) => {
        if (cancelled) return;
        if (source.kind === 'link') {
          setState({ kind: 'ready', url: source.url });
          return;
        }
        objectUrl = URL.createObjectURL(source.blob);
        setState({ kind: 'ready', url: objectUrl });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Could not open the record.',
        });
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [apiUrl, submissionId, doc.id]);

  // Focus the way out, and hand focus back to whatever opened the viewer.
  useEffect(() => {
    const opener = globalThis.document?.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => opener?.focus?.();
  }, []);

  const zoom = ZOOM_STEPS[zoomIndex]!;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="vax-viewer-title"
      className="ferro-vax-viewer"
      data-testid="vax-document-viewer"
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        onClose();
      }}
    >
      <header className="ferro-vax-viewer__bar">
        <div className="ferro-vax-viewer__row">
          <div className="ferro-vax-viewer__heading">
            <h2 id="vax-viewer-title" className="ferro-vax-viewer__title">
              {title}
            </h2>
            {doc.filename ? (
              <p className="ferro-vax-viewer__filename">{doc.filename}</p>
            ) : null}
          </div>
          <button
            ref={closeRef}
            type="button"
            className="ferro-btn ferro-btn--primary ferro-vax-viewer__close"
            onClick={onClose}
            data-testid="vax-viewer-close"
          >
            Close
          </button>
        </div>
        {state.kind === 'ready' ? (
          <div className="ferro-vax-viewer__tools">
            {!isPdf ? (
              <div className="ferro-vax-viewer__zoom" role="group" aria-label="Zoom">
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}
                  disabled={zoomIndex === 0}
                  aria-label="Zoom out"
                >
                  −
                </button>
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={() => setZoomIndex(0)}
                  aria-label="Fit to screen"
                  data-testid="vax-viewer-fit"
                >
                  {zoom === 1 ? 'Fit' : `${Math.round(zoom * 100)}%`}
                </button>
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={() => setZoomIndex((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))}
                  disabled={zoomIndex === ZOOM_STEPS.length - 1}
                  aria-label="Zoom in"
                  data-testid="vax-viewer-zoom-in"
                >
                  +
                </button>
              </div>
            ) : null}
            <a
              className="ferro-btn ferro-btn--ghost"
              href={state.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in new tab
            </a>
          </div>
        ) : null}
      </header>

      <div
        className={`ferro-vax-viewer__stage${zoom > 1 ? ' ferro-vax-viewer__stage--zoomed' : ''}`}
      >
        {state.kind === 'loading' ? (
          <p className="ferro-vax-viewer__status" role="status">
            Opening the record…
          </p>
        ) : state.kind === 'error' ? (
          <p className="ferro-vax-viewer__status" role="alert">
            {state.message}
          </p>
        ) : isPdf ? (
          <iframe
            className="ferro-vax-viewer__pdf"
            src={state.url}
            title={title}
            data-testid="vax-viewer-pdf"
          />
        ) : (
          <img
            className="ferro-vax-viewer__image"
            src={state.url}
            alt={title}
            data-testid="vax-viewer-image"
            style={zoom > 1 ? { width: `${zoom * 100}%`, maxWidth: 'none', maxHeight: 'none' } : undefined}
            // A tap on the fitted record is the natural "let me read that".
            onClick={() => setZoomIndex((i) => (i === 0 ? 2 : 0))}
          />
        )}
      </div>
    </div>
  );
}
